<template>
  <v-container id="thaid-callback" fluid tag="section" class="text_google">
    <v-row justify="center" align="center" style="min-height: 60vh">
      <v-col cols="12" md="7" lg="5">
        <base-material-card color="primary">
          <template v-slot:heading>
            <div class="font-weight-light text-center">
              <h3>ยืนยันตัวตนผ่าน ThaiID</h3>
            </div>
          </template>

          <v-card-text class="text-center py-8">
            <template v-if="status === 'working'">
              <v-progress-circular indeterminate color="primary" size="56"></v-progress-circular>
              <p class="mt-4">กำลังตรวจสอบผลการยืนยันตัวตน...</p>
            </template>

            <template v-else-if="status === 'success'">
              <v-icon color="success" size="56">mdi-check-circle</v-icon>
              <p class="mt-4">{{ successMessage }}</p>
            </template>

            <template v-else>
              <v-icon :color="errorDetail.color" size="56">{{ errorDetail.icon }}</v-icon>

              <div class="mt-3">
                <v-chip :color="errorDetail.color" outlined small class="category-chip">
                  <v-icon left x-small>{{ errorDetail.icon }}</v-icon>
                  สาเหตุ: {{ errorDetail.category }}
                </v-chip>
              </div>

              <h3 class="mt-4 error-title">{{ errorDetail.title }}</h3>
              <p class="mt-2 error-message">{{ errorDetail.message }}</p>

              <v-alert
                v-if="errorDetail.action"
                dense
                outlined
                :color="errorDetail.color"
                class="mt-3 text-left action-alert"
              >
                <v-icon left small :color="errorDetail.color">mdi-arrow-right-circle-outline</v-icon>
                <strong>ขั้นตอนที่ควรทำ:</strong> {{ errorDetail.action }}
              </v-alert>

              <v-btn color="primary" rounded to="/pages/login" class="mt-4">
                กลับหน้าเข้าสู่ระบบ
              </v-btn>

              <div class="error-code-ref mt-3">รหัสอ้างอิง: {{ rawErrorCode || "-" }}</div>
            </template>
          </v-card-text>
        </base-material-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
// ThaidCallback.vue — ปลายทางหลัง DOPA redirect กลับมา (callback_thiaid.php เป็นคนส่งมาที่นี่)
// รับ ?claim=... (สำเร็จ) หรือ ?error=... (ล้มเหลว/ยกเลิก) แล้วแลก claim เป็น session จริง
// ผ่าน thaid_claim.exchange.php ครั้งเดียว จากนั้นเก็บ localStorage แบบเดียวกับ login เดิมทุกจุด
const VALID_PERSONNEL_STATUSES = [
  "teacher", "se_director", "director", "S", "38", "3801", "3802", "3803", "C", "gov_em"
];
const VALID_ETC_M_TYPES = [
  "governmentteach", "governmentsp", "government", "temporary", "temporary_job", "permanent"
];
const USER_REDIRECT_MAP = {
  A: "/admin",
  B: "/college",
  C: "/iven",
  D: "/personnel",
  G: "/vecprovince",
  S: "/supervisions"
};

// ── หมวดหมู่สาเหตุ — ใช้แยกให้ผู้ใช้งานเห็นชัดว่า "ปัญหาเกิดจากระบบไหน" ไม่ใช่แค่ข้อความรวมๆ ──
const CATEGORY = {
  THAID: { category: "ระบบ ThaiID (กรมการปกครอง)", color: "deep-orange", icon: "mdi-qrcode-scan" },
  HRVEC: { category: "ระบบ HRVEC (ขั้นตอนยืนยันตัวตน)", color: "blue-grey", icon: "mdi-web" },
  PERSONNEL_DATA: { category: "ข้อมูลบุคลากรในระบบ (personnel_etc)", color: "orange darken-1", icon: "mdi-account-search-outline" },
  ACCOUNT_LINK: { category: "การผูกบัญชีกับ ThaiID", color: "red darken-1", icon: "mdi-link-variant-off" },
  ACCOUNT_ACCESS: { category: "สิทธิ์การใช้งานบัญชี", color: "primary", icon: "mdi-account-question-outline" }
};

const DEFAULT_ERROR_DETAIL = {
  ...CATEGORY.HRVEC,
  title: "เกิดข้อผิดพลาดในการยืนยันตัวตน",
  message: "ไม่สามารถยืนยันตัวตนผ่าน ThaiID ได้ในขณะนี้",
  action: "กรุณาเข้าสู่ระบบใหม่อีกครั้ง หากยังพบปัญหาซ้ำ กรุณาแจ้งผู้ดูแลระบบพร้อมรหัสอ้างอิงด้านล่าง"
};

const ERROR_MESSAGES = {
  // ค่าที่ไม่รู้จัก/ไม่อยู่ใน whitelist ของ callback_thiaid.php จะตกมาที่นี่ (fallback ทั่วไป)
  thaid_denied: {
    ...CATEGORY.THAID,
    title: "การยืนยันตัวตนผ่าน ThaiID ไม่สำเร็จ",
    message: "ระบบ ThaiID ปฏิเสธคำขอยืนยันตัวตน โดยไม่ได้ระบุสาเหตุที่ชัดเจน",
    action: "กรุณาเข้าสู่ระบบใหม่อีกครั้ง"
  },
  // ค่าจาก DOPA โดยตรง ตรงตามเอกสาร "API ลงทะเบียนสำหรับผู้ให้บริการ (RP)" รุ่น 1.2.0 หัวข้อ 7 —
  // ผ่าน whitelist ใน callback_thiaid.php แล้ว (ไม่ใช่ generic OAuth2 list — DOPA ใช้ 'user_denied'
  // สำหรับกรณีผู้ใช้กดยกเลิกจริงๆ ไม่ใช่ 'access_denied' ซึ่งความหมายจริงคือ "ไม่มีสิทธิ์เข้าถึง")
  thaid_user_denied: {
    ...CATEGORY.THAID,
    title: "ท่านยกเลิกการยืนยันตัวตน",
    message: "ท่านกดยกเลิกขั้นตอนการยืนยันตัวตนบนแอป/เว็บของ ThaiID",
    action: "กรุณาเข้าสู่ระบบใหม่ และสแกน/ยืนยันตัวตนผ่าน ThaiID ให้ครบขั้นตอน"
  },
  thaid_unauthorized_client: {
    ...CATEGORY.THAID,
    title: "ระบบยังไม่ได้รับอนุญาตจากกรมการปกครอง",
    message: "การลงทะเบียน client_id/redirect_uri ของ HRVEC กับ DOPA มีปัญหา (unauthorized_client)",
    action: "กรุณาแจ้งผู้ดูแลระบบให้ตรวจสอบการลงทะเบียนกับ DOPA — ไม่ใช่ปัญหาที่ตัวท่านทำผิด"
  },
  thaid_invalid_client: {
    ...CATEGORY.THAID,
    title: "ไม่พบ client_id ที่ลงทะเบียนไว้",
    message: "ระบบ HRVEC ส่ง client_id ที่ DOPA ไม่รู้จัก (invalid_client)",
    action: "กรุณาแจ้งผู้ดูแลระบบ — ไม่ใช่ปัญหาที่ตัวท่านทำผิด"
  },
  thaid_access_denied: {
    ...CATEGORY.THAID,
    title: "ไม่มีสิทธิ์เข้าถึงบริการยืนยันตัวตน",
    message: "บัญชีนี้ไม่มีสิทธิ์เข้าถึงบริการยืนยันตัวตนของ DOPA",
    action: "กรุณาติดต่อผู้ดูแลระบบ"
  },
  thaid_verify_permission_denied: {
    ...CATEGORY.THAID,
    title: "ไม่มีสิทธิ์ใช้การยืนยันตัวตนด้วยภาพใบหน้า",
    message: "หน่วยงานยังไม่มีสิทธิ์ใช้การยืนยันตัวตนด้วยภาพใบหน้าจาก DOPA",
    action: "กรุณาแจ้งผู้ดูแลระบบ"
  },
  thaid_invalid_request: {
    ...CATEGORY.THAID,
    title: "คำขอไปยัง ThaiID ไม่ถูกต้อง",
    message: "รูปแบบคำขอที่ระบบ HRVEC ส่งไปยัง ThaiID ไม่ถูกต้อง (invalid_request)",
    action: "กรุณาแจ้งผู้ดูแลระบบ — ไม่ใช่ปัญหาที่ตัวท่านทำผิด"
  },
  thaid_invalid_scope: {
    ...CATEGORY.THAID,
    title: "ขอบเขตข้อมูลที่ขอไม่ถูกต้อง",
    message: "ขอบเขตข้อมูล (scope) ที่ระบบ HRVEC ขอจาก ThaiID ไม่ถูกต้อง",
    action: "กรุณาแจ้งผู้ดูแลระบบ"
  },
  thaid_unsupported_response_type: {
    ...CATEGORY.THAID,
    title: "รูปแบบคำขอไม่ได้รับการรองรับ",
    message: "unsupported_response_type จากฝั่ง ThaiID",
    action: "กรุณาแจ้งผู้ดูแลระบบ"
  },
  thaid_unsupported_grant_type: {
    ...CATEGORY.THAID,
    title: "รูปแบบการขอ token ไม่ได้รับการรองรับ",
    message: "unsupported_grant_type จากฝั่ง ThaiID",
    action: "กรุณาแจ้งผู้ดูแลระบบ"
  },
  thaid_unsupported_token_hint: {
    ...CATEGORY.THAID,
    title: "รูปแบบคำขอ token ไม่ได้รับการรองรับ",
    message: "unsupported_token_hint จากฝั่ง ThaiID",
    action: "กรุณาแจ้งผู้ดูแลระบบ"
  },
  thaid_unsupported_token_type: {
    ...CATEGORY.THAID,
    title: "ประเภท token ไม่ได้รับการรองรับ",
    message: "unsupported_token_type จากฝั่ง ThaiID",
    action: "กรุณาแจ้งผู้ดูแลระบบ"
  },
  thaid_duplicate_request: {
    ...CATEGORY.THAID,
    title: "มีการส่งคำขอซ้ำ",
    message: "ระบบ ThaiID ได้รับคำขอซ้ำสำหรับการยืนยันตัวตนครั้งนี้",
    action: "กรุณาเข้าสู่ระบบใหม่อีกครั้ง"
  },
  thaid_invalid_refresh_token: {
    ...CATEGORY.THAID,
    title: "การยืนยันตัวตนหมดอายุ",
    message: "invalid_refresh_token จากฝั่ง ThaiID",
    action: "กรุณาเข้าสู่ระบบใหม่อีกครั้ง"
  },
  thaid_server_error: {
    ...CATEGORY.THAID,
    title: "ระบบ ThaiID ขัดข้องชั่วคราว",
    message: "เซิร์ฟเวอร์ของกรมการปกครองขัดข้องชั่วคราว (server_error)",
    action: "กรุณาลองเข้าสู่ระบบใหม่อีกครั้งในอีกสักครู่"
  },
  // ค่าจากฝั่ง callback_thiaid.php เอง (ไม่ใช่จาก DOPA โดยตรง)
  missing_code_or_state: {
    ...CATEGORY.HRVEC,
    title: "ข้อมูลจาก ThaiID ไม่ครบถ้วน",
    message: "ระบบไม่ได้รับข้อมูลยืนยันตัวตนครบตามที่กำหนดจาก ThaiID",
    action: "กรุณาเข้าสู่ระบบใหม่อีกครั้ง"
  },
  invalid_or_expired_state: {
    ...CATEGORY.HRVEC,
    title: "ลิงก์ยืนยันตัวตนหมดอายุหรือถูกใช้ไปแล้ว",
    message: "ลิงก์ยืนยันตัวตนนี้ถูกใช้ไปแล้ว หรือเปิดค้างไว้นานเกินไปจนหมดอายุ (5 นาที)",
    action: "กรุณากลับไปกรอกรหัสผ่านและเข้าสู่ระบบใหม่อีกครั้ง"
  },
  token_exchange_failed: {
    ...CATEGORY.HRVEC,
    title: "เชื่อมต่อระบบ ThaiID ไม่สำเร็จ",
    message: "ระบบ HRVEC เชื่อมต่อไปยังเซิร์ฟเวอร์ ThaiID เพื่อแลก token ไม่สำเร็จ",
    action: "กรุณาลองเข้าสู่ระบบใหม่อีกครั้ง หากยังไม่สำเร็จ กรุณาแจ้งผู้ดูแลระบบ"
  },
  identity_verification_failed: {
    ...CATEGORY.HRVEC,
    title: "ไม่สามารถยืนยันตัวตนผ่าน ThaiID ได้",
    message: "ระบบไม่สามารถตรวจสอบผลการยืนยันตัวตนที่ได้รับกลับมาได้",
    action: "กรุณาลองเข้าสู่ระบบใหม่อีกครั้ง"
  },
  identity_mismatch: {
    ...CATEGORY.ACCOUNT_LINK,
    title: "ThaiID ไม่ตรงกับที่เคยผูกไว้กับบัญชีนี้",
    message: "ยืนยันตัวตนผ่าน ThaiID สำเร็จ แต่ ThaiID นี้ไม่ตรงกับ ThaiID ที่เคยผูกไว้กับบัญชีนี้มาก่อน",
    action: "กรุณาติดต่อผู้ดูแลระบบ"
  },
  // ขั้นตอน ThaiID สำเร็จจริง (ไม่ใช่ปัญหาฝั่ง ThaiID) — แต่ไม่พบเลขบัตรประชาชนนี้ในระบบข้อมูลบุคลากร
  // (personnel_etc) ของสถานศึกษา/สอจ. นี้ จึงต้องแยกหมวดหมู่ออกจาก identity_mismatch โดยเจาะจงบอกว่า
  // เป็นปัญหาที่ "ข้อมูลบุคลากรในระบบ" ไม่ใช่ ThaiID และบอกขั้นตอนแก้ไขที่ทำได้เอง
  not_affiliated_personnel_etc: {
    ...CATEGORY.PERSONNEL_DATA,
    title: "ไม่พบข้อมูลของท่านในระบบบุคลากรของหน่วยงานนี้",
    message:
      "ยืนยันตัวตนผ่าน ThaiID สำเร็จ (ไม่ใช่ปัญหาของ ThaiID) แต่ไม่พบเลขบัตรประชาชนนี้ในระบบข้อมูลบุคลากร " +
      "(personnel_etc) ของสถานศึกษา/หน่วยงานนี้",
    action: "กรุณาติดต่องานบุคลากรของสถานศึกษา/หน่วยงานท่าน เพื่อตรวจสอบหรือเพิ่มข้อมูลท่านในระบบก่อน จึงจะยืนยันตัวตนผ่าน ThaiID เข้าใช้งานบัญชีนี้ได้"
  },
  thaid_already_linked: {
    ...CATEGORY.ACCOUNT_LINK,
    title: "ThaiID นี้ถูกผูกกับบัญชีอื่นไปแล้ว",
    message: "ThaiID ที่ใช้ยืนยันตัวตนนี้ถูกผูกไว้กับบัญชีอื่นในระบบไปแล้ว",
    action: "กรุณาติดต่อผู้ดูแลระบบ"
  },
  unknown_subject_type: {
    ...CATEGORY.ACCOUNT_ACCESS,
    title: "ไม่รู้จักประเภทบัญชีนี้",
    message: "ระบบไม่รู้จักประเภทบัญชีที่กำลังเข้าสู่ระบบ",
    action: "กรุณาติดต่อผู้ดูแลระบบ"
  },
  // ── รหัสภายในหน้านี้เอง (ไม่ได้มาจาก ?error= ของ callback_thiaid.php) — เพิ่มเพื่อให้ทุกจุดที่ทำให้
  // เข้าระบบไม่ได้ แสดงหมวดหมู่/สาเหตุแบบเดียวกันหมด ไม่มีจุดไหนตกไปใช้ข้อความเปล่าๆ ──
  missing_claim: {
    ...CATEGORY.HRVEC,
    title: "ไม่พบข้อมูลยืนยันตัวตน",
    message: "หน้านี้ถูกเปิดโดยไม่มีข้อมูลยืนยันตัวตนที่ถูกต้องแนบมาด้วย",
    action: "กรุณาเข้าสู่ระบบใหม่อีกครั้งจากหน้าเข้าสู่ระบบ ไม่ควรเปิดลิงก์นี้ตรงๆ"
  },
  claim_expired: {
    ...CATEGORY.HRVEC,
    title: "ลิงก์ยืนยันตัวตนหมดอายุหรือถูกใช้ไปแล้ว",
    message: "รหัสยืนยันตัวตนชั่วคราวนี้ใช้ได้ครั้งเดียวและหมดอายุภายใน 60 วินาที",
    action: "กรุณากลับไปกรอกรหัสผ่านและเข้าสู่ระบบใหม่อีกครั้ง"
  },
  unsupported_account_type: {
    ...CATEGORY.ACCOUNT_ACCESS,
    title: "บัญชีนี้ยังไม่รองรับการเข้าสู่ระบบผ่านหน้านี้",
    message: "ประเภทบัญชีนี้ยังไม่รองรับการเข้าสู่ระบบผ่าน ThaiID ในหน้านี้",
    action: "กรุณาติดต่อผู้ดูแลระบบ"
  },
  client_exception: {
    ...CATEGORY.HRVEC,
    title: "เกิดข้อผิดพลาดในการเข้าสู่ระบบ",
    message: "เกิดข้อผิดพลาดขณะติดต่อระบบ HRVEC เพื่อแลกข้อมูลยืนยันตัวตน",
    action: "กรุณาลองเข้าสู่ระบบใหม่อีกครั้ง หากยังพบปัญหาซ้ำ กรุณาแจ้งผู้ดูแลระบบ"
  }
};

export default {
  data() {
    return {
      status: "working",
      successMessage: "",
      errorDetail: DEFAULT_ERROR_DETAIL,
      rawErrorCode: ""
    };
  },
  async mounted() {
    const { claim, error } = this.$route.query;

    if (error) {
      this.setError(error);
      return;
    }

    if (!claim) {
      this.setError("missing_claim");
      return;
    }

    try {
      const result = await this.$http.post("thaid_claim.exchange.php", { claim });

      if (!result.data || !result.data.session_token) {
        this.setError("claim_expired");
        return;
      }

      const user = result.data;
      user.system_lock = false;
      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("force_password_change", user.must_change_password ? "1" : "0");

      const target = this.resolveTarget(user);
      if (!target) {
        localStorage.clear();
        this.setError("unsupported_account_type");
        return;
      }

      this.status = "success";
      this.successMessage = user.thaid_first_link
        ? "ผูกบัญชีนี้กับ ThaiID ของคุณเรียบร้อยแล้ว กำลังเข้าสู่ระบบ..."
        : "ยืนยันตัวตนสำเร็จ กำลังเข้าสู่ระบบ...";

      setTimeout(() => {
        this.$router.push(target);
      }, 1200);
    } catch (e) {
      this.setError("client_exception");
    }
  },
  methods: {
    setError(code) {
      this.status = "error";
      this.rawErrorCode = code || "";
      this.errorDetail = (code && ERROR_MESSAGES[code]) || DEFAULT_ERROR_DETAIL;
    },
    resolveTarget(user) {
      switch (user.thaid_subject_type) {
        case "personnel_temporary":
          return VALID_PERSONNEL_STATUSES.includes(user.user_status) ? "/user" : null;
        case "personnel_etc":
          return VALID_ETC_M_TYPES.includes(user.personnel_etc_m_type) ? "/userEtc" : null;
        case "assistantteach":
          return user.user_status === "teach" ? "/assistantTeach" : null;
        case "user":
          return USER_REDIRECT_MAP[user.user_status] || null;
        default:
          // veccentraldep/veccentral: ยังไม่มีหน้า login ของ Vue เรียกใช้ subject_type เหล่านี้
          // (ดู M6 ในแผน) — ตกมาที่นี่แปลว่ายังไม่รองรับการ redirect ต่อในหน้านี้
          return null;
      }
    }
  }
};
</script>

<style>
.text_google {
  font-family: "Kanit", sans-serif;
}
</style>

<style scoped>
.category-chip {
  font-weight: 600;
}

.error-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #263238;
}

.error-message {
  font-size: 0.92rem;
  line-height: 1.6;
  color: #455a64;
}

.action-alert {
  border-radius: 8px;
  font-size: 0.88rem;
  line-height: 1.6;
}

.error-code-ref {
  font-size: 0.72rem;
  color: #9e9e9e;
  font-family: monospace;
}
</style>
